import { ActionTree } from 'vuex';
import {
  EResourceLibraryAction,
  EResourceLibraryMutation,
  state as defaultState,
} from '@/content-library/resource-library/store/resource-library.constants';
import { IResourceLibraryState } from '@/content-library/resource-library/store/resource-library.types';
import { RootState } from '@/store/store';
import { ResourceService } from '@/api/resources/services/resource.service';
import { IResource } from '@/api/resources/types/resource';
import { ILoadResourcesPayload } from '@/content-library/types/load-resources-payload';
import { ILoadResourcesFilter } from '@/content-library/types/load-resources-filter';
import { APIErrorMessages } from '@/api/constants/api-errors.constants';
import { IDocument } from '@/types/Document';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { IVideo } from '@/types/Video';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { VideoService } from '@/api/video/services/video.service';
import { DocumentService } from '@/api/documents/services/document.service';
import { IBreadcrumb } from '@common/components/base-breadcrumbs/types/Breadcrumb';

export const actions: ActionTree<IResourceLibraryState, RootState> = {
  async [EResourceLibraryAction.Load]({ state, commit, dispatch }) {
    commit(EResourceLibraryMutation.SetLoading, true);
    commit(EResourceLibraryMutation.SetLoaded, false);
    const { limit, filter, currentPage } = state.payload;
    const { searchTerms, sortBy, sortOrder, type, directory } = filter;
    const offset: number = limit * (currentPage - 1);
    let resources: IResource[] = [];
    let totalCount = 0;
    try {
      const response: {
        resources: IResource[];
        totalCount: number;
      } = await ResourceService.loadList(
        searchTerms,
        limit,
        offset,
        sortBy,
        sortOrder,
        type,
        directory
      );
      // eslint-disable-next-line prefer-destructuring
      resources = response.resources;
      // eslint-disable-next-line prefer-destructuring
      totalCount = response.totalCount;
    } catch {
      dispatch(EResourceLibraryAction.LoadFailed);
    }
    const mappedResources: IResource[] = await Promise.all(
      resources.map(
        async (resource: IResource): Promise<IResource> => {
          let block: Partial<IContentLinkBlock | IVideo | IDocument>;
          switch (type) {
            case EResourceType.ContentLink: {
              try {
                block = await ContentLinkBlockService.loadThumbnail(resource.contentId);
              } catch {
                console.error(`Could not ${EResourceType.ContentLink} thumbnail`);
              }
              break;
            }
            case EResourceType.Video: {
              try {
                block = await VideoService.loadThumbnail(resource.contentId);
              } catch {
                console.error(`Could not ${EResourceType.Video} thumbnail`);
              }
              break;
            }
            case EResourceType.Template: {
              try {
                block = await DocumentService.loadTemplateResourceDetails(resource.contentId);
              } catch (e) {
                const isAccessDenied = e?.message === APIErrorMessages.AccessDenied;
                if (isAccessDenied) {
                  return { ...resource } as IResource;
                } else {
                  console.error(`Could not ${EResourceType.Template} details`, e);
                }
              }
              break;
            }
            default:
              break;
          }
          return { ...resource, block } as IResource;
        }
      )
    );
    dispatch(EResourceLibraryAction.LoadSuccess, {
      mappedResources,
      totalCount,
    });
  },
  [EResourceLibraryAction.LoadSuccess](
    { commit },
    { mappedResources, totalCount }: { mappedResources: IResource[]; totalCount: number }
  ) {
    commit(EResourceLibraryMutation.SetResources, { mappedResources, totalCount });
    commit(EResourceLibraryMutation.SetLoading, false);
    commit(EResourceLibraryMutation.SetLoaded, true);
  },
  [EResourceLibraryAction.LoadFailed]({ commit }) {
    commit(EResourceLibraryMutation.SetLoading, false);
    commit(EResourceLibraryMutation.SetLoaded, false);
  },
  [EResourceLibraryAction.SetPayload](
    { commit, dispatch, state },
    payload: Partial<ILoadResourcesPayload>
  ) {
    commit(EResourceLibraryMutation.SetPayload, { ...state.payload, ...payload });
    dispatch(EResourceLibraryAction.Load);
  },
  [EResourceLibraryAction.Reset]({ commit }) {
    commit(EResourceLibraryMutation.SetState, defaultState);
  },
  [EResourceLibraryAction.UpdateFilter](
    { commit, dispatch, state },
    filter: Partial<ILoadResourcesFilter>
  ) {
    commit(EResourceLibraryMutation.SetFilter, { ...state.payload.filter, ...filter });
    dispatch(EResourceLibraryAction.Load);
  },
  [EResourceLibraryAction.UpdateBreadcrumbs]({ commit }, breadcrumbs: string[]) {
    const newCrumbs: IBreadcrumb[] = breadcrumbs.map(
      (crumb: string, index: number): IBreadcrumb => {
        return {
          label: index === 0 ? '' : crumb,
          icon: index === 0 ? 'folder' : '',
        };
      }
    );
    commit(EResourceLibraryMutation.SetBreadcrumbs, newCrumbs);
  },
};
